/**
 * External URL Apps Service
 *
 * Reads external URL apps from the BFF backend (via /apps endpoint).
 * The BFF joins available_apps with app_external_urls and returns
 * external_url in the response.
 *
 * Module-level cache avoids repeated API round-trips per openWindow call.
 */

import { backendAppsService, type ExternalOpenMode } from './backend-apps-service'
import { logError, logInfo } from '@/lib/logger'

export interface ExternalUrlApp {
  app_name: string
  external_url: string
  open_mode: ExternalOpenMode
  sso: boolean
  display_name: string
  icon: string
  category: string
}

let cache: ExternalUrlApp[] | null = null

export const externalUrlAppsService = {
  async getAll(): Promise<ExternalUrlApp[]> {
    if (cache) return cache
    try {
      const apps = await backendAppsService.getActiveApps()
      const result: ExternalUrlApp[] = apps
        .filter((a) => a.external_url)
        .map((a) => ({
          app_name: a.name,
          external_url: a.external_url!,
          // Rows from before migration 078 have no mode; they opened in a tab.
          open_mode: a.open_mode ?? 'tab',
          sso: a.sso ?? false,
          display_name: a.display_name || a.name,
          icon: a.icon || 'Globe',
          category: a.category || 'productivity',
        }))
      cache = result
      logInfo({ context: 'ExternalUrlAppsService' }, `Loaded ${result.length} external URL apps`)
      return result
    } catch (error) {
      logError(error as Error, { context: 'ExternalUrlAppsService.getAll' })
      return []
    }
  },

  async getApp(appName: string): Promise<ExternalUrlApp | null> {
    const apps = await this.getAll()
    return apps.find((a) => a.app_name === appName) ?? null
  },

  /** Synchronous read for render paths; null until getAll() has loaded once. */
  getCachedApp(appName: string): ExternalUrlApp | null {
    return cache?.find((a) => a.app_name === appName) ?? null
  },

  invalidateCache(): void {
    cache = null
  },
}
